import React, { useState } from 'react';
import { Input, Button, Tag, Spin, Drawer, Card, Typography, Statistic, Badge, Tooltip, Pagination, Row, Col, Select } from 'antd';
import { SearchOutlined, ReloadOutlined, EyeOutlined, UserOutlined, DollarOutlined, CheckCircleOutlined, ExclamationCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useQuery } from 'react-query';
import { debtService, Debt } from '../../services/debtService';
import { customerService, Customer } from '../../services/customerService';
import DebtDetailModalMobile from '../../components/debts/DebtDetailModalMobile';
import { calculateGroupedDebtStatus, getDebtStatusColor, getDebtStatusText } from '../../utils/debtStatusUtils';
import styles from './DebtsPageMobile.module.css';

const { Text } = Typography;
const { Option } = Select;

const statusIcon = {
  unpaid: <ExclamationCircleOutlined />,
  partial: <ClockCircleOutlined />,
  paid: <CheckCircleOutlined />,
};

const DebtsPageMobile: React.FC = () => {
  const [selectedDebt, setSelectedDebt] = useState<Debt | null>(null);
  const [customerId, setCustomerId] = useState<string | undefined>(undefined);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all'); // Thêm state cho bộ lọc trạng thái
  const [customerDebts, setCustomerDebts] = useState<Debt[]>([]);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Lấy danh sách khách hàng cho filter
  const { data: customers = [], isLoading: isLoadingCustomers } = useQuery(
    'customers-for-select',
    () => customerService.getCustomersForSelect()
  );

  // Lấy danh sách công nợ
  const { data: debts = [], isLoading, refetch } = useQuery(
    ['debts', customerId],
    () => debtService.getDebts(customerId)
  );

  // Mapping customerId -> customer object for fast lookup
  const customerMap = React.useMemo(() => {
    const map: Record<string, Customer> = {};
    customers.forEach((c: Customer) => {
      map[c.id] = c;
    });
    return map;
  }, [customers]);

  // Gom nhóm công nợ theo khách hàng
  const groupedDebts = React.useMemo(() => {
    const map: Record<string, { customer: any, totalDebt: number, totalPaid: number, remain: number, status: string, latestCreated: string } > = {};
    debts.forEach((d: any) => {
      const key = String(d.customer_id);
      if (!map[key]) {
        // Sử dụng thông tin khách hàng từ API response thay vì customerMap
        const customerInfo = {
          id: d.customer_id,
          name: d.customer_name || `Khách hàng ${d.customer_id}`,
          code: d.customer_code || d.customer_id,
          phone: d.customer_phone,
          address: d.customer_address
        };
        map[key] = {
          customer: customerInfo,
          totalDebt: 0,
          totalPaid: 0,
          remain: 0,
          status: 'unpaid',
          latestCreated: d.created_at
        };
      }
      map[key].totalDebt += d.debt_amount;
      map[key].totalPaid += d.paid_amount;
      map[key].remain += (d.debt_amount - d.paid_amount);
      if (d.created_at > map[key].latestCreated) map[key].latestCreated = d.created_at;
    });

    // Xác định trạng thái tổng hợp sau khi đã tính toán xong
    Object.values(map).forEach((group) => {
      const statusInfo = calculateGroupedDebtStatus([
        { debt_amount: group.totalDebt, paid_amount: group.totalPaid }
      ]);
      group.status = statusInfo.status;
      group.remain = statusInfo.remain;
    });

    return Object.entries(map).map(([customer_id, v]) => ({
      customer_id,
      ...v
    }));
  }, [debts]);

  // Filtered data
  const filteredDebts = React.useMemo(() => {
    return groupedDebts.filter((d) => {
      // Filter theo trạng thái
      if (statusFilter !== 'all' && d.status !== statusFilter) {
        return false;
      }
      
      // Filter theo search text
      if (searchText) {
        // Nếu searchText là số, so sánh với customer_id
        if (!isNaN(Number(searchText))) {
          return String(d.customer_id).includes(searchText);
        }
        // Nếu searchText là text, so sánh với tên khách hàng hoặc mã khách hàng
        if (d.customer) {
          const customerName = d.customer.name?.toLowerCase() || '';
          const customerCode = d.customer.code?.toLowerCase() || '';
          const searchLower = searchText.toLowerCase();
          return customerName.includes(searchLower) || customerCode.includes(searchLower);
        }
        return false;
      }
      return true;
    });
  }, [groupedDebts, statusFilter, searchText]);

  // Thống kê theo bộ lọc hiện tại
  const filteredStats = React.useMemo(() => {
    const totalDebt = filteredDebts.reduce((sum: number, d: any) => sum + d.totalDebt, 0);
    const totalPaid = filteredDebts.reduce((sum: number, d: any) => sum + d.totalPaid, 0);
    const totalRemain = filteredDebts.reduce((sum: number, d: any) => sum + d.remain, 0);
    return { totalDebt, totalPaid, totalRemain };
  }, [filteredDebts]);

  // Thống kê
  const totalDebt = filteredStats.totalDebt;
  const totalPaid = filteredStats.totalPaid;
  const totalRemain = filteredStats.totalRemain;

  // Đếm đúng số khách nợ theo bộ lọc
  const totalCustomers = filteredDebts.length;

  // Không phân trang, hiển thị toàn bộ danh sách
  const pagedDebts = filteredDebts;

  const handleViewDetail = async (r: any) => {
    setLoadingDetail(true);
    // Lọc tất cả debts của khách hàng này
    const debtsOfCustomer = debts.filter((d: Debt) => String(d.customer_id) === String(r.customer_id));
    // Fetch chi tiết từng khoản nợ (bao gồm payments)
    const allDebtsWithPayments = await Promise.all(
      debtsOfCustomer.map(async (d) => {
        try {
          const detail = await debtService.getDebtDetail(d.id);
          return { ...d, payments: detail.payments };
        } catch {
          return { ...d, payments: [] };
        }
      })
    );
    setCustomerDebts(allDebtsWithPayments);
    setSelectedDebt({ ...allDebtsWithPayments[0], customer_id: r.customer_id });
    setLoadingDetail(false);
  };

  return (
    <div className={styles['debts-mobile-container']}>
      {/* HEADER */}
      <header className={styles['debts-mobile-header']}>
        {/* Thống kê tổng quan */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 8 }}>
          <div style={{ background: '#fff', borderRadius: 10, padding: '8px 4px', display: 'flex', flexDirection: 'column', alignItems: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>Tổng khách nợ</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}><UserOutlined style={{ color: '#1890ff', fontSize: 16 }} /><span style={{ fontWeight: 700, color: '#1890ff', fontSize: 16 }}>{totalCustomers}</span></div>
          </div>
          <div style={{ background: '#fff', borderRadius: 10, padding: '8px 4px', display: 'flex', flexDirection: 'column', alignItems: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>Tổng số nợ</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}><DollarOutlined style={{ color: '#faad14', fontSize: 16 }} /><span style={{ fontWeight: 700, color: '#faad14', fontSize: 16 }}>{totalDebt.toLocaleString('vi-VN')}</span></div>
          </div>
          <div style={{ background: '#fff', borderRadius: 10, padding: '8px 4px', display: 'flex', flexDirection: 'column', alignItems: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>Đã trả</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}><CheckCircleOutlined style={{ color: '#52c41a', fontSize: 16 }} /><span style={{ fontWeight: 700, color: '#52c41a', fontSize: 16 }}>{totalPaid.toLocaleString('vi-VN')}</span></div>
          </div>
          <div style={{ background: '#fff', borderRadius: 10, padding: '8px 4px', display: 'flex', flexDirection: 'column', alignItems: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>Còn lại</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}><ExclamationCircleOutlined style={{ color: '#ff4d4f', fontSize: 16 }} /><span style={{ fontWeight: 700, color: '#ff4d4f', fontSize: 16 }}>{totalRemain.toLocaleString('vi-VN')}</span></div>
          </div>
        </div>
        {/* Thanh tìm kiếm */}
        <div style={{ marginBottom: 8 }}>
          <Input
            className={styles['debts-mobile-search']}
            style={{ width: '100%', padding: '8px 12px', border: '1px solid #ced4da', borderRadius: 8, fontSize: 16, boxSizing: 'border-box' }}
            placeholder="Tìm kiếm khách hàng..."
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            allowClear
            size="large"
          />
        </div>
        {/* Bộ lọc trạng thái */}
        <div style={{ marginBottom: 8 }}>
          <Select
            style={{ width: '100%', borderRadius: 8 }}
            placeholder="Trạng thái nợ"
            value={statusFilter}
            onChange={v => setStatusFilter(v || 'all')}
            allowClear
            size="large"
          >
            <Option value="all">Tất cả trạng thái</Option>
            <Option value="unpaid">Chưa trả</Option>
            <Option value="partial">Còn nợ</Option>
            <Option value="paid">Đã trả</Option>
          </Select>
        </div>
      </header>
      <Spin spinning={isLoading || loadingDetail}>
        <div className={styles['debts-mobile-list']}>
          {pagedDebts.map((item: any) => (
            <div
              key={item.customer_id}
              className={styles['debts-mobile-card']}
              style={{
                maxWidth: 420,
                width: '100%',
                background: '#fff',
                borderRadius: 16,
                boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                border: '1px solid #e5e7eb',
                margin: '0 auto 4px',
                overflow: 'hidden',
              }}
            >
              <div style={{ padding: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontWeight: 700, fontSize: 18, color: '#1890ff', textAlign: 'left' }}>{item.customer?.name || `Khách hàng ${item.customer_id}`}</div>
                    <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4, textAlign: 'left' }}>Mã KH: {item.customer?.code || item.customer_id}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <Tag color={getDebtStatusColor(item.status as 'unpaid' | 'partial' | 'paid')} icon={statusIcon[item.status as keyof typeof statusIcon]} style={{ margin: 0 }}>
                      {getDebtStatusText(item.status as 'unpaid' | 'partial' | 'paid')}
                    </Tag>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 15, textAlign: 'left' }}>
                  <div><Text strong>Số nợ:</Text> <span style={{ color: '#faad14', fontWeight: 600 }}>{item.totalDebt.toLocaleString('vi-VN')} VNĐ</span></div>
                  <div><Text strong>Đã trả:</Text> <span style={{ color: '#52c41a', fontWeight: 600 }}>{item.totalPaid.toLocaleString('vi-VN')} VNĐ</span></div>
                  <div><Text strong>Còn lại:</Text> <span style={{ color: '#ff4d4f', fontWeight: 600 }}>{item.remain.toLocaleString('vi-VN')} VNĐ</span></div>
                </div>
              </div>
              <div style={{ background: '#f9fafb', padding: '12px 24px', borderTop: '1px solid #e5e7eb' }}>
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8 }}>
                  <button onClick={() => handleViewDetail(item)} className={styles['debts-mobile-view-btn']} style={{ padding: 8, borderRadius: '50%', color: '#1890ff', background: 'none', border: 'none', fontSize: 20, transition: 'all 0.2s' }}>
                    <EyeOutlined />
                  </button>
                </div>
              </div>
            </div>
          ))}
          {pagedDebts.length === 0 && (
            <div className={styles['debts-mobile-empty']}>Không có khách nợ nào phù hợp.</div>
          )}
        </div>
      </Spin>
      {/* Pagination đã bị loại bỏ, hiển thị toàn bộ danh sách */}
      {/* Drawer chi tiết */}
      {selectedDebt && (
        <DebtDetailModalMobile
          debt={selectedDebt}
          onClose={() => setSelectedDebt(null)}
          onPaid={() => {
            setSelectedDebt(null);
            refetch();
          }}
          customers={customers}
          allDebts={customerDebts}
        />
      )}
    </div>
  );
};

export default DebtsPageMobile; 